import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  ButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, startOfWeek, endOfWeek, subWeeks, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { enGB } from 'date-fns/locale';
import { apiGet } from '../../utils/api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const TASK_CATEGORIES = {
  Volumetrie: {
    'Lucrari noi introduse in CRM': null,
    'Email-uri trimise': [
      'Intern Comenzi CRM',
      'Intern Solicitare Ofertă',
      'Intern Administrativ',
      'Extern Ofertare',
      'Extern Prezentare',
      'Extern Concurență',
      'Extern Colaborare',
    ],
    Telefoane: null,
    Prospectie: ['Online', 'Teren'],
    Rapoarte: null,
    Intalniri: null,
    Sedinte: null,
    Altele: null,
  },
  Rezultate: {
    'Contracte de colaborare noi': [
      'Prospectie Online',
      'Teren',
      'Clienți mulțumiți',
      'Cunoștințe',
      'Alta sursă',
      'Soft',
    ],
    'Proiecte noi': [
      'Colaborator existent',
      'Colaborator nou',
      'Client mulțumit',
      'Cunoștințe',
      'Soft',
    ],
    Prospectie: ['Teren', 'Online'],
  },
  'Amprenta Personala': {
    'Informatii Concurenta (Brand / Sistem)': null,
    'Obstacole Intampinate': null,
    'Feedback client ( Negativ / Pozitiv )': null,
    'Propuneri si Idei': null,
  },
};

interface ReportTask {
  category: string;
  name: string;
  type: string;
  totalQuantity: number;
  totalTime: number;
}

interface ClientWithStages {
  id: number;
  name: string;
  jobId: string;
  stages: Array<{
    stageType: string;
    date: string;
    column1: string;
    column2: string;
  }>;
}

interface HierarchyUser {
  id: number;
  username: string;
  level: number;
  managerId: number | null;
  createdAt: string;
}

const ReportPage: React.FC = () => {
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [tasks, setTasks] = useState<ReportTask[]>([]);
  const [clients, setClients] = useState<ClientWithStages[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hierarchyUsers, setHierarchyUsers] = useState<HierarchyUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const fetchTasks = async (start: Date, end: Date, showLoading: boolean = true) => {
    if (showLoading) {
      setLoading(true);
    }
    setError(null);
    try {
      const formattedStartDate = format(start, 'dd-MM-yyyy');
      const formattedEndDate = format(end, 'dd-MM-yyyy');
      
      let data;
      if (selectedUserId) {
        // Get tasks for specific user
        data = await apiGet(`/task/user/${selectedUserId}/range/${formattedStartDate}/${formattedEndDate}`);
      } else {
        // Get tasks for current user
        data = await apiGet(`/task/range/${formattedStartDate}/${formattedEndDate}`);
      }
      setTasks(data);
    } catch (err: any) {
      setError(err.message);
      setTasks([]);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const fetchClientsWithStages = async (start: Date, end: Date) => {
    try {
      const formattedStartDate = format(start, 'yyyy-MM-dd');
      const formattedEndDate = format(end, 'yyyy-MM-dd');
      
      let data;
      if (selectedUserId) {
        // Get clients for specific user
        data = await apiGet(`/client/report/user/${selectedUserId}/range?startDate=${formattedStartDate}&endDate=${formattedEndDate}`);
      } else {
        // Get clients for current user
        data = await apiGet(`/client/report/range?startDate=${formattedStartDate}&endDate=${formattedEndDate}`);
      }
      
      // Filter stages to only include those within the date range
      const filteredClients = data.map((client: ClientWithStages) => ({
        ...client,
        stages: client.stages.filter(stage => {
          const stageDate = new Date(stage.date);
          // Before comparing, extract only the date part (YYYY-MM-DD)
          const stageDateStr = stageDate.toISOString().slice(0, 10);
          const startStr = start.toISOString().slice(0, 10);
          const endStr = end.toISOString().slice(0, 10);
          return stageDateStr >= startStr && stageDateStr <= endStr;
        })
      })).filter((client: ClientWithStages) => client.stages.length > 0); // Only keep clients that have stages in the range
      
      // Sort clients by number of stages in descending order
      const sortedClients = filteredClients.sort((a: ClientWithStages, b: ClientWithStages) => {
        return b.stages.length - a.stages.length;
      });
      
      setClients(sortedClients);
    } catch (err: any) {
      console.error('Error fetching clients with stages:', err);
      setClients([]);
    }
  };

  // Sort tasks to maintain the order from TASK_CATEGORIES and filter out Amprenta Personala
  const sortTasksByCategoryOrder = (tasksToSort: ReportTask[]): ReportTask[] => {
    // Filter out Amprenta Personala category
    const filteredTasks = tasksToSort.filter(task => task.category !== 'Amprenta Personala');
    
    // Create a map of category and task name to their order in TASK_CATEGORIES
    const orderMap = new Map<string, number>();
    let orderIndex = 0;
    
    Object.entries(TASK_CATEGORIES).forEach(([category, categoryData]) => {
      // Skip Amprenta Personala category
      if (category === 'Amprenta Personala') return;
      
      Object.entries(categoryData).forEach(([name, types]) => {
        const key = `${category}|${name}`;
        orderMap.set(key, orderIndex++);
      });
    });
    
    return filteredTasks.sort((a, b) => {
      const keyA = `${a.category}|${a.name}`;
      const keyB = `${b.category}|${b.name}`;
      const orderA = orderMap.get(keyA) ?? Number.MAX_SAFE_INTEGER;
      const orderB = orderMap.get(keyB) ?? Number.MAX_SAFE_INTEGER;
      
      // If same category and name, sort by type alphabetically
      if (orderA === orderB) {
        return (a.type || '').localeCompare(b.type || '');
      }
      
      return orderA - orderB;
    });
  };

  // Sort tasks to maintain the original order from TASK_CATEGORIES and filter out Amprenta Personala
  const displayTasks = sortTasksByCategoryOrder(tasks);

  // Calculate total time for the summary row
  const totalTime = displayTasks.reduce((sum, task) => sum + task.totalTime, 0);
  const totalMinutes = totalTime;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const timeDisplay = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

  // Chart data preparation
  const prepareCategoryChartData = () => {
    const categoryData = displayTasks.reduce((acc, task) => {
      if (!acc[task.category]) {
        acc[task.category] = 0;
      }
      acc[task.category] += task.totalTime;
      return acc;
    }, {} as Record<string, number>);

    return {
      labels: Object.keys(categoryData),
      datasets: [
        {
          label: 'Timp (min)',
          data: Object.values(categoryData),
          backgroundColor: 'rgba(54, 162, 235, 0.8)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
        },
      ],
    };
  };

  const prepareTopTasksChartData = () => {
    const taskData = displayTasks
      .filter(task => task.totalTime > 0)
      .sort((a, b) => b.totalTime - a.totalTime)
      .slice(0, 8);

    return {
      labels: taskData.map(task => `${task.name}${task.type ? ` - ${task.type}` : ''}`),
      datasets: [
        {
          label: 'Timp (min)',
          data: taskData.map(task => task.totalTime),
          backgroundColor: 'rgba(75, 192, 192, 0.8)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
        },
      ],
    };
  };

  // Client charts data preparation
  const prepareClientActivityChartData = () => {
    const clientData = clients
      .sort((a, b) => b.stages.length - a.stages.length)
      .slice(0, 8);

    return {
      labels: clientData.map(client => client.name),
      datasets: [
        {
          label: 'Număr Etape',
          data: clientData.map(client => client.stages.length),
          backgroundColor: 'rgba(255, 159, 64, 0.8)',
          borderColor: 'rgba(255, 159, 64, 1)',
          borderWidth: 1,
        },
      ],
    };
  };

  const prepareStageDistributionChartData = () => {
    const stageCounts = clients.reduce((acc, client) => {
      client.stages.forEach(stage => {
        if (!acc[stage.stageType]) {
          acc[stage.stageType] = 0;
        }
        acc[stage.stageType]++;
      });
      return acc;
    }, {} as Record<string, number>);

    const colors = [
      'rgba(255, 99, 132, 0.8)',
      'rgba(54, 162, 235, 0.8)',
      'rgba(255, 205, 86, 0.8)',
      'rgba(75, 192, 192, 0.8)',
      'rgba(153, 102, 255, 0.8)',
      'rgba(255, 159, 64, 0.8)',
    ];

    return {
      labels: Object.keys(stageCounts),
      datasets: [
        {
          data: Object.values(stageCounts),
          backgroundColor: colors.slice(0, Object.keys(stageCounts).length),
          borderWidth: 2,
          borderColor: '#fff',
        },
      ],
    };
  };



  const fetchHierarchyUsers = async () => {
    try {
      const data = await apiGet('/auth/users/hierarchy');
      setHierarchyUsers(data);
    } catch (err: any) {
      console.error('Error fetching hierarchy users:', err);
      setHierarchyUsers([]);
    }
  };

  useEffect(() => {
    fetchHierarchyUsers();
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchTasks(startDate, endDate);
      fetchClientsWithStages(startDate, endDate);
    }
  }, [startDate, endDate, selectedUserId]);



  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date);
  };

  // Preset date range handlers
  const handleCurrentWeek = () => {
    const now = new Date();
    setStartDate(startOfWeek(now, { weekStartsOn: 1 })); // Monday start
    setEndDate(endOfWeek(now, { weekStartsOn: 1 }));
  };

  const handleLastWeek = () => {
    const now = new Date();
    const lastWeek = subWeeks(now, 1);
    setStartDate(startOfWeek(lastWeek, { weekStartsOn: 1 })); // Monday start
    setEndDate(endOfWeek(lastWeek, { weekStartsOn: 1 }));
  };

  const handleLastMonth = () => {
    const now = new Date();
    const lastMonth = subMonths(now, 1);
    setStartDate(startOfMonth(lastMonth));
    setEndDate(endOfMonth(lastMonth));
  };

  const handleCurrentMonth = () => {
    const now = new Date();
    setStartDate(startOfMonth(now));
    setEndDate(now); // Today
  };

  const exportToPDF = async () => {
    const reportElement = document.getElementById('report-container');
    if (!reportElement) return;

    try {
      // Show loading state
      const exportButton = document.getElementById('export-pdf-btn');
      if (exportButton) {
        exportButton.textContent = 'Se exportă...';
        exportButton.setAttribute('disabled', 'true');
      }

      // Wait a bit for charts to render properly
      await new Promise(resolve => setTimeout(resolve, 1000));

      const canvas = await html2canvas(reportElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: reportElement.scrollWidth,
        height: reportElement.scrollHeight,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      // Add title
      pdf.setFontSize(20);
      pdf.text('Raport', 105, 15, { align: 'center' });
      pdf.setFontSize(12);
      pdf.text(`Perioada: ${startDate && format(startDate, 'dd/MM/yyyy')} - ${endDate && format(endDate, 'dd/MM/yyyy')}`, 105, 25, { align: 'center' });
      pdf.text(`Generat la: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 105, 32, { align: 'center' });

      // Add the image
      pdf.addImage(imgData, 'PNG', 0, 40, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add new pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Generate filename
      const filename = `raport_${format(startDate || new Date(), 'yyyy-MM-dd')}_${format(endDate || new Date(), 'yyyy-MM-dd')}.pdf`;
      
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Eroare la generarea PDF-ului. Încercați din nou.');
    } finally {
      // Reset button state
      const exportButton = document.getElementById('export-pdf-btn');
      if (exportButton) {
        exportButton.textContent = 'Export PDF';
        exportButton.removeAttribute('disabled');
      }
    }
  };

  return (
    <Box>
      {/* Header with date controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h5" fontWeight={700}>
              Raport
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <ButtonGroup size="small" variant="outlined">
                <Button onClick={handleCurrentWeek}>Săptămâna curentă</Button>
                <Button onClick={handleLastWeek}>Ultima săptămână</Button>
                <Button onClick={handleLastMonth}>Ultima lună</Button>
                <Button onClick={handleCurrentMonth}>Luna curentă</Button>
              </ButtonGroup>
              <Button
                id="export-pdf-btn"
                variant="contained"
                color="primary"
                onClick={exportToPDF}
                disabled={loading}
                sx={{ ml: 2 }}
              >
                Export PDF
              </Button>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Raportul meu</InputLabel>
              <Select
                value={selectedUserId || ''}
                onChange={(e) => setSelectedUserId(e.target.value ? Number(e.target.value) : null)}
              >
                <MenuItem value="">
                  <em>Raportul meu</em>
                </MenuItem>
                {hierarchyUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.username} (Level {user.level})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { minWidth: 150 },
                  },
                }}
              />
              <Typography variant="body1" color="text.secondary">
                to
              </Typography>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { minWidth: 150 },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        </CardContent>
      </Card>

      {/* Main content container for PDF export */}
      <Box id="report-container">

      {/* Main content grid */}
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
        {/* Left column - Reports */}
        <Box>
          {/* Tasks Report */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={2}>
                Oglinda Activitate
              </Typography>

              {loading && (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {!loading && (
                <TableContainer component={Paper}>
                  <Table sx={{ borderCollapse: 'collapse' }} size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ p: '2px', fontSize: '0.75rem' }}>Categorie</TableCell>
                        <TableCell sx={{ p: '2px', fontSize: '0.75rem' }}>Task</TableCell>
                        <TableCell sx={{ p: '2px', fontSize: '0.75rem' }}>Tip</TableCell>
                        <TableCell sx={{ p: '2px', fontSize: '0.75rem' }}>Cantitate</TableCell>
                        <TableCell sx={{ p: '2px', fontSize: '0.75rem' }}>Timp</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayTasks.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 2 }}>
                            <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                              No tasks found for this date range
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        displayTasks.map((task, index) => {
                          // Define background color and borders based on category
                          const getRowStyle = (): React.CSSProperties => {
                            const baseStyle: React.CSSProperties = {
                              border: 'none',
                            };
                            
                            switch (task.category) {
                              case 'Volumetrie':
                                return { 
                                  ...baseStyle,
                                  backgroundColor: '#f5f5f5' // light gray
                                };
                              case 'Rezultate':
                                return { 
                                  ...baseStyle,
                                  backgroundColor: '#f0f8f0' // light green
                                };
                              case 'Amprenta Personala':
                                return { 
                                  ...baseStyle,
                                  backgroundColor: '#f0f8ff' // light blue
                                };
                              default:
                                return baseStyle;
                            }
                          };
                          
                          // Define border styles for category and task cells
                          const getCategoryCellStyle = () => {
                            const baseStyle = getRowStyle();
                            const isLastOfCategory = index === displayTasks.length - 1 || displayTasks[index + 1].category !== task.category;
                            
                            return {
                              ...baseStyle,
                              borderBottom: isLastOfCategory ? '2px solid #ccc' : 'none',
                              borderRight: '1px solid #e0e0e0',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                            };
                          };
                          
                          const getTaskCellStyle = () => {
                            const baseStyle = getRowStyle();
                            const isLastOfTask = index === displayTasks.length - 1 || 
                              displayTasks[index + 1].category !== task.category || 
                              displayTasks[index + 1].name !== task.name;
                            
                            return {
                              ...baseStyle,
                              borderBottom: isLastOfTask ? '2px solid #ccc' : 'none',
                              borderRight: '1px solid #e0e0e0',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                            };
                          };
                          
                          // Check if this is the first task of its category
                          const isFirstOfCategory = index === 0 || displayTasks[index - 1].category !== task.category;
                          
                          // Check if this is the first task of its name within the category
                          const isFirstOfTaskName = isFirstOfCategory || displayTasks[index - 1].name !== task.name;
                          
                          return (
                            <TableRow key={`${task.category}-${task.name}-${task.type}`}>
                              <TableCell sx={{ ...getCategoryCellStyle(), p: '2px' }}>
                                {isFirstOfCategory ? task.category : ''}
                              </TableCell>
                              <TableCell sx={{ ...getTaskCellStyle(), p: '2px' }}>
                                {isFirstOfTaskName ? task.name : ''}
                              </TableCell>
                              <TableCell sx={{ borderRight: '1px solid #e0e0e0', p: '2px', fontSize: '0.75rem' }}>
                                {task.type || ''}
                              </TableCell>
                              <TableCell sx={{ borderRight: '1px solid #e0e0e0', p: '2px', fontSize: '0.75rem' }}>
                                {task.totalQuantity > 0 ? task.totalQuantity : ''}
                              </TableCell>
                              <TableCell sx={{ p: '2px', fontSize: '0.75rem' }}>
                                {task.totalTime > 0 ? task.totalTime : ''}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                      {/* Summary row with total time */}
                      {displayTasks.length > 0 && (
                        <TableRow sx={{ backgroundColor: '#000', color: '#fff' }}>
                          <TableCell 
                            colSpan={3} 
                            sx={{ 
                              p: '2px', 
                              color: '#fff', 
                              fontWeight: 700,
                              borderTop: '2px solid #333',
                              fontSize: '0.75rem'
                            }}
                          >
                            TOTAL TIMP
                          </TableCell>
                          <TableCell 
                            sx={{ 
                              p: '2px', 
                              color: '#fff', 
                              fontWeight: 700,
                              borderTop: '2px solid #333',
                              borderRight: '1px solid #333',
                              fontSize: '0.75rem'
                            }}
                          >
                            {totalMinutes} min
                          </TableCell>
                          <TableCell 
                            sx={{ 
                              p: '2px', 
                              color: '#fff', 
                              fontWeight: 700,
                              borderTop: '2px solid #333',
                              fontSize: '0.75rem'
                            }}
                          >
                            {timeDisplay}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Client Pipeline Report */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={2}>
                Palnie Clienti
              </Typography>
              
              {!loading && (
                <Box>
                  <Typography variant="body2" mb={2} fontSize="0.75rem">
                    Clienți cu etape în perioada {startDate && format(startDate, 'dd/MM/yyyy')} - {endDate && format(endDate, 'dd/MM/yyyy')}:
                  </Typography>
                  
                  {clients.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" textAlign="center" py={2} fontSize="0.75rem">
                      Nu există clienți cu etape în această perioadă
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Nume Client</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>ID Lucrare</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Etape</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {clients.map((client) => {
                            // Check if any stage has column2 value "anulat" (case insensitive)
                            const hasAnulat = client.stages.some(stage => 
                              stage.column2 && stage.column2.toLowerCase() === 'anulat'
                            );
                            // Check if client is contracted (has "DA" in contractare stage)
                            const contractareStage = client.stages.find(stage => 
                              stage.stageType && stage.stageType.toLowerCase() === 'contractare'
                            );
                            const isContracted = contractareStage && (
                              (contractareStage.column1 && contractareStage.column1.toUpperCase() === 'DA') || 
                              (contractareStage.column2 && contractareStage.column2.toUpperCase() === 'DA')
                            );
                            
                            // Debug logging
                            if (hasAnulat || isContracted) {
                              console.log(`Client ${client.name}:`, {
                                hasAnulat,
                                isContracted,
                                stages: client.stages.map(s => ({
                                  stageType: s.stageType,
                                  column1: s.column1,
                                  column2: s.column2
                                }))
                              });
                            }
                            
                            // Determine row background color - green takes priority over red
                            let backgroundColor = 'inherit';
                            let hoverBackgroundColor = undefined;
                            
                            if (isContracted) {
                              backgroundColor = '#e8f5e8';
                              hoverBackgroundColor = '#d4edda';
                            } else if (hasAnulat) {
                              backgroundColor = '#ffebee';
                              hoverBackgroundColor = '#ffcdd2';
                            }
                            
                            return (
                              <TableRow 
                                key={client.id} 
                                hover
                                sx={{
                                  backgroundColor: backgroundColor,
                                  '&:hover': {
                                    backgroundColor: hoverBackgroundColor
                                  }
                                }}
                              >
                                <TableCell sx={{ fontSize: '0.75rem' }}>{client.name}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem' }}>{client.jobId}</TableCell>
                              <TableCell>
                                {client.stages.map((stage, index) => (
                                  <Box key={index} sx={{ mb: 1, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                    <Typography variant="body2" fontWeight={500} fontSize="0.7rem" mb={0.5}>
                                      {stage.stageType}: {stage.date}
                                    </Typography>
                                    <Box display="flex" gap={2} alignItems="center">
                                      <Box display="flex" alignItems="center" gap={0.5}>
                                        <Typography variant="body2" fontSize="0.6rem" color="text.secondary">
                                        </Typography>
                                        {stage.column1 ? (
                                          <Box display="flex" alignItems="center" gap={0.5}>
                                            {stage.column1 === 'red' && <span style={{ color: '#ff0000', fontSize: '0.8rem' }}>★</span>}
                                            {stage.column1 === 'orange' && <span style={{ color: '#ff8c00', fontSize: '0.8rem' }}>★</span>}
                                            {stage.column1 === 'blue' && <span style={{ color: '#0066cc', fontSize: '0.8rem' }}>★</span>}
                                            {stage.column1 === 'white' && <span style={{ color: '#ffffff', fontSize: '0.8rem', textShadow: '1px 1px 1px #000000' }}>★</span>}
                                            {!['red', 'orange', 'blue', 'white'].includes(stage.column1) && (
                                              <Typography variant="body2" fontSize="0.6rem" color="text.secondary">
                                                {stage.column1}
                                              </Typography>
                                            )}
                                          </Box>
                                        ) : (
                                          <Typography variant="body2" fontSize="0.6rem" color="text.secondary">-</Typography>
                                        )}
                                      </Box>
                                      <Box display="flex" alignItems="center" gap={0.5}>
                                        <Typography variant="body2" fontSize="0.6rem" color="text.secondary">
                                        </Typography>
                                        {stage.column2 ? (
                                          <Box display="flex" alignItems="center" gap={0.5}>
                                            {stage.column2 === 'red' && <span style={{ color: '#ff0000', fontSize: '0.8rem' }}>★</span>}
                                            {stage.column2 === 'orange' && <span style={{ color: '#ff8c00', fontSize: '0.8rem' }}>★</span>}
                                            {stage.column2 === 'blue' && <span style={{ color: '#0066cc', fontSize: '0.8rem' }}>★</span>}
                                            {stage.column2 === 'white' && <span style={{ color: '#ffffff', fontSize: '0.8rem', textShadow: '1px 1px 1px #000000' }}>★</span>}
                                            {!['red', 'orange', 'blue', 'white'].includes(stage.column2) && (
                                              <Typography variant="body2" fontSize="0.6rem" color="text.secondary">
                                                {stage.column2}
                                              </Typography>
                                            )}
                                          </Box>
                                        ) : (
                                          <Typography variant="body2" fontSize="0.6rem" color="text.secondary">-</Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  </Box>
                                ))}
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Right column - Charts */}
        <Box>
          {/* First row - Task Charts */}
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} sx={{ mb: 3 }}>
            {/* Category Performance Chart */}
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={2}>
                  1. Timp pe Categorii
                </Typography>
                <Box sx={{ height: 250 }}>
                  <Bar 
                    data={prepareCategoryChartData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false,
                        },
                        title: {
                          display: false,
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                        },
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>

            {/* Top Tasks Chart */}
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={2}>
                  2. Top Sarcini (Timp)
                </Typography>
                <Box sx={{ height: 250 }}>
                  <Bar 
                    data={prepareTopTasksChartData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      indexAxis: 'y' as const,
                      plugins: {
                        legend: {
                          display: false,
                        },
                        title: {
                          display: false,
                        },
                      },
                      scales: {
                        x: {
                          beginAtZero: true,
                        },
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Second row - Client Charts */}
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            {/* Client Activity Chart */}
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={2}>
                  3. Top Clienți (Etape)
                </Typography>
                <Box sx={{ height: 250 }}>
                  <Bar 
                    data={prepareClientActivityChartData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      indexAxis: 'y' as const,
                      plugins: {
                        legend: {
                          display: false,
                        },
                        title: {
                          display: false,
                        },
                      },
                      scales: {
                        x: {
                          beginAtZero: true,
                        },
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>

            {/* Stage Distribution Chart */}
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={2}>
                  4. Distribuția Etape
                </Typography>
                <Box sx={{ height: 250 }}>
                  <Doughnut 
                    data={prepareStageDistributionChartData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom' as const,
                        },
                        title: {
                          display: false,
                        },
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
      </Box>
    </Box>
  );
};

export default ReportPage;
